import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  Drawer,
  Group,
  Modal,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconAlertCircle, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { Controller, type FieldPath, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DayInput } from '../../inputs/DayInput.js';
import { useUnsavedChanges } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import {
  type EquipmentFormValues,
  emptyEquipmentForm,
  equipmentFormOf,
  equipmentFormSchema,
} from './equipment-form.js';
import classes from './Pms.module.css';
import { errorDetail } from './source.js';
import type { EquipmentDetail, EquipmentNode } from './types.js';

const NODE_TYPES = ['group', 'system', 'equipment', 'component'] as const;
const STATUSES = ['operational', 'operational_with_restrictions', 'not_operational'] as const;
const SURVEY = ['pms_ce', 'surveyor', 'condition_monitoring', 'none'] as const;

/** Sunucu hata kodunun metni; alan ayrıntısı (`code:REQUIRED`) varsa eklenir. */
export function usePmsErrorText() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = errorCode(error);
    const text = t(`pms.errors.${code}`, { defaultValue: t('pms.errors.GENERIC') });
    if (code !== 'INVALID_EQUIPMENT' && code !== 'INVALID_VESSEL_PARTICULARS') return text;
    const fields = (errorDetail(error) ?? '')
      .split(',')
      .map((part) => part.split(':')[0])
      .filter(Boolean);
    return fields.length > 0 ? `${text} (${fields.join(', ')})` : text;
  };
}

export interface EquipmentDrawerProps {
  opened: boolean;
  /** `null`: yeni düğüm. */
  detail: EquipmentDetail | null;
  /** Yeni düğümün önerilen üstü (seçili düğüm). */
  parent: EquipmentNode | null;
  nodes: readonly EquipmentNode[];
  /** Geminin yerel günü: kurulum tarihi gelecekte olamaz. */
  today: string;
  canDecommission: boolean;
  onClose: () => void;
  onSubmit: (values: EquipmentFormValues) => Promise<void>;
  onDecommission: (reason: string) => Promise<void>;
}

/**
 * Add / Edit Equipment çekmecesi (PMS belgesi §4–§5; plan Task 6b): React Hook Form ve Task 1'in
 * Zod şeması. Kimlik, sınıflandırma (kritik [ISM 10.3], klas, PMS dışı gerekçesi K25), çalışma
 * saati (sayaç, günlük sınır K5, Noon Report eşlemesi K2), teknik bilgiler ve notlar. Düzenlemede
 * gerekçeli "Remove from register" (K26). Kaydedilmemiş değişiklikle çıkış onay ister.
 */
export function EquipmentDrawer({
  opened,
  detail,
  parent,
  nodes,
  today,
  canDecommission,
  onClose,
  onSubmit,
  onDecommission,
}: EquipmentDrawerProps) {
  const { t } = useTranslation();
  const k = (key: string) => t(`pms.drawer.${key}`);
  const errorText = usePmsErrorText();
  const [error, setError] = useState<unknown>(null);
  const [removing, setRemoving] = useState(false);
  const form = useForm<EquipmentFormValues>({
    resolver: zodResolver(equipmentFormSchema(today)),
    defaultValues: emptyEquipmentForm(null),
  });
  const { errors, isSubmitting, isDirty } = form.formState;
  useUnsavedChanges(opened && isDirty);

  useEffect(() => {
    if (!opened) return;
    setError(null);
    form.reset(
      detail
        ? equipmentFormOf(detail)
        : emptyEquipmentForm(parent?.id ?? null, parent?.nodeType ?? null),
    );
  }, [opened, detail, parent, form]);

  const fieldError = (name: FieldPath<EquipmentFormValues> | 'noonReportSource') => {
    const parts = name.split('.');
    let node: unknown = errors;
    for (const part of parts) node = (node as Record<string, unknown> | undefined)?.[part];
    const message = (node as { message?: string } | undefined)?.message;
    return message
      ? t(`pms.fieldErrors.${message}`, { defaultValue: t('pms.errors.VALIDATION_FAILED') })
      : undefined;
  };

  // Kendisi ve alt düğümleri üst olamaz (EQUIPMENT_CYCLE); sunucu yeniden denetler
  const excluded = new Set<string>();
  if (detail) {
    excluded.add(detail.id);
    let grew = true;
    while (grew) {
      grew = false;
      for (const n of nodes) {
        if (n.parentId && excluded.has(n.parentId) && !excluded.has(n.id)) {
          excluded.add(n.id);
          grew = true;
        }
      }
    }
  }
  // Bileşen üst seçilmez; var olan kaydın üstü bileşense o da listede görünür (alan boş kalmaz)
  const parentOptions = [
    { value: '', label: k('parentNone') },
    ...nodes
      .filter(
        (n) => !excluded.has(n.id) && (n.nodeType !== 'component' || n.id === detail?.parentId),
      )
      .map((n) => ({ value: n.id, label: `${n.name} · ${n.code}` })),
  ];

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      await onSubmit(values);
    } catch (failure) {
      setError(failure);
    }
  });

  const text = (name: FieldPath<EquipmentFormValues>, label: string, required = false) => (
    <TextInput
      label={label}
      required={required}
      {...form.register(name)}
      error={fieldError(name)}
    />
  );
  const number = (
    name: 'maxDailyHours' | `technical.${'powerKw' | 'rpm' | 'cylinders'}`,
    label: string,
    extra: { description?: string; decimalScale?: number } = {},
  ) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <NumberInput
          label={label}
          description={extra.description}
          decimalScale={extra.decimalScale ?? 1}
          min={0}
          value={field.value as number | ''}
          onChange={(value) => field.onChange(typeof value === 'number' ? value : '')}
          onBlur={field.onBlur}
          error={fieldError(name)}
        />
      )}
    />
  );
  const toggle = (name: FieldPath<EquipmentFormValues>, label: string, description?: string) => (
    <Controller
      control={form.control}
      name={name}
      render={({ field }) => (
        <Switch
          label={label}
          description={description}
          checked={field.value === true}
          onChange={(event) => field.onChange(event.currentTarget.checked)}
        />
      )}
    />
  );

  const critical = form.watch('critical');
  const outOfPms = form.watch('outOfPmsManual');
  const hasCounter = form.watch('hasCounter');
  const noonMode = form.watch('noonMode');

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        position="right"
        size={560}
        title={detail ? k('editTitle') : k('addTitle')}
        closeButtonProps={{ 'aria-label': k('close') }}
        classNames={{ title: classes.drawerTitle }}
      >
        <form onSubmit={submit} noValidate className={classes.drawerForm}>
          <Stack gap="md">
            {error ? (
              <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
                {errorText(error)}
              </Alert>
            ) : null}

            <fieldset className={classes.fieldset}>
              <legend>{k('sections.identity')}</legend>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <Controller
                  control={form.control}
                  name="parentId"
                  render={({ field }) => (
                    <Select
                      label={k('parent')}
                      data={parentOptions}
                      searchable
                      allowDeselect={false}
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? '')}
                      error={fieldError('parentId')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="nodeType"
                  render={({ field }) => (
                    <Select
                      label={k('nodeType')}
                      data={NODE_TYPES.map((value) => ({
                        value,
                        label: t(`pms.nodeTypes.${value}`),
                      }))}
                      allowDeselect={false}
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 'equipment')}
                      error={fieldError('nodeType')}
                    />
                  )}
                />
                {text('code', k('code'), true)}
                {text('name', k('name'), true)}
                {text('tag', k('tag'))}
                {text('sfiCode', k('sfiCode'))}
                {text('location', k('location'))}
                {text('maker', k('maker'))}
                {text('model', k('model'))}
                {text('serialNo', k('serialNo'))}
                <Controller
                  control={form.control}
                  name="installedOn"
                  render={({ field }) => (
                    <DayInput
                      label={k('installedOn')}
                      value={field.value || null}
                      maxDate={today}
                      onChange={(value) => field.onChange(value ?? '')}
                      error={fieldError('installedOn')}
                    />
                  )}
                />
                <Controller
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      label={k('status')}
                      data={STATUSES.map((value) => ({
                        value,
                        label: t(`pms.statuses.${value}`),
                      }))}
                      allowDeselect={false}
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? 'operational')}
                    />
                  )}
                />
              </SimpleGrid>
            </fieldset>

            <fieldset className={classes.fieldset}>
              <legend>{k('sections.classification')}</legend>
              <Stack gap="sm">
                {toggle('critical', k('critical'), k('criticalHint'))}
                {critical ? text('criticalReason', k('criticalReason')) : null}
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  {toggle('classRelevant', k('classRelevant'))}
                  {toggle('ismRelevant', k('ismRelevant'))}
                  {toggle('safetyCritical', k('safetyCritical'))}
                </SimpleGrid>
                {toggle('outOfPmsManual', k('outOfPmsManual'))}
                {outOfPms ? text('outOfPmsReason', k('outOfPmsReason'), true) : null}
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                  {text('bvItemCode', k('bvItemCode'))}
                  <Controller
                    control={form.control}
                    name="surveyPrinciple"
                    render={({ field }) => (
                      <Select
                        label={k('surveyPrinciple')}
                        data={SURVEY.map((value) => ({
                          value,
                          label: t(`pms.surveyPrinciples.${value}`),
                        }))}
                        clearable
                        value={field.value || null}
                        onChange={(value) => field.onChange(value ?? '')}
                      />
                    )}
                  />
                </SimpleGrid>
              </Stack>
            </fieldset>

            <fieldset className={classes.fieldset}>
              <legend>{k('sections.counter')}</legend>
              <Stack gap="sm">
                {toggle('hasCounter', k('hasCounter'))}
                {hasCounter
                  ? number('maxDailyHours', k('maxDailyHours'), {
                      description: k('maxDailyHoursHint'),
                    })
                  : null}
                <Controller
                  control={form.control}
                  name="noonMode"
                  render={({ field }) => (
                    <Stack gap={4}>
                      <span className={classes.fieldLabel} id="noon-source-label">
                        {k('noonReportSource')}
                      </span>
                      <SegmentedControl
                        aria-labelledby="noon-source-label"
                        value={field.value}
                        onChange={field.onChange}
                        data={[
                          { value: 'none', label: k('noonNone') },
                          { value: 'main_engine', label: k('noonMain') },
                          { value: 'auxiliary', label: k('noonAuxiliary') },
                        ]}
                      />
                      {fieldError('noonMode') ? (
                        <span className={classes.fieldError}>{fieldError('noonMode')}</span>
                      ) : null}
                    </Stack>
                  )}
                />
                {noonMode === 'auxiliary'
                  ? text('noonAuxName', k('noonAuxiliaryName'), true)
                  : null}
              </Stack>
            </fieldset>

            <fieldset className={classes.fieldset}>
              <legend>{k('sections.technical')}</legend>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                {number('technical.powerKw', k('powerKw'))}
                {number('technical.rpm', k('rpm'))}
                {text('technical.fuelType', k('fuelType'))}
                {number('technical.cylinders', k('cylinders'), { decimalScale: 0 })}
                {text('technical.coolingType', k('coolingType'))}
                {text('technical.lubricationType', k('lubricationType'))}
                {text('technical.startingSystem', k('startingSystem'))}
              </SimpleGrid>
            </fieldset>

            <Textarea
              label={k('notes')}
              autosize
              minRows={3}
              maxRows={8}
              {...form.register('notes')}
              error={fieldError('notes')}
            />

            <Group justify="space-between" className={classes.drawerFooter}>
              {detail && canDecommission ? (
                <Button
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={16} aria-hidden />}
                  onClick={() => setRemoving(true)}
                >
                  {t('pms.equipment.details.decommission')}
                </Button>
              ) : (
                <span />
              )}
              <Group gap="sm">
                <Button variant="default" onClick={onClose}>
                  {k('cancel')}
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  {k('save')}
                </Button>
              </Group>
            </Group>
          </Stack>
        </form>
      </Drawer>
      {detail ? (
        <DecommissionDialog
          opened={removing}
          name={detail.name}
          onClose={() => setRemoving(false)}
          onConfirm={async (reason) => {
            await onDecommission(reason);
            setRemoving(false);
          }}
        />
      ) : null}
    </>
  );
}

/** Kayıttan kaldırma (K26): gerekçe zorunlu; geçmiş görünür kalır [NR496 4.3.2a]. */
function DecommissionDialog({
  opened,
  name,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  name: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const k = (key: string, values: Record<string, unknown> = {}) =>
    t(`pms.decommission.${key}`, values);
  const errorText = usePmsErrorText();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (opened) {
      setReason('');
      setError(null);
    }
  }, [opened]);
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={k('title', { name })}
      closeButtonProps={{ 'aria-label': k('close') }}
    >
      <form
        onSubmit={async (event) => {
          event.preventDefault();
          if (reason.trim() === '') {
            setError({ code: 'DECOMMISSION_REASON_REQUIRED' });
            return;
          }
          setBusy(true);
          setError(null);
          try {
            await onConfirm(reason.trim());
          } catch (failure) {
            setError(failure);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Stack gap="sm">
          <p className={classes.modalIntro}>{k('intro')}</p>
          {error ? (
            <Alert color="red" icon={<IconAlertCircle size={18} />} role="alert">
              {errorText(error)}
            </Alert>
          ) : null}
          <Textarea
            label={k('reason')}
            required
            autosize
            minRows={2}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>
              {k('cancel')}
            </Button>
            <Button type="submit" color="red" loading={busy}>
              {k('confirm')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
